import { Economy } from '@audigoal/core';
import { db } from './db';
import type { Profile } from './types';

/**
 * The day's running totals: what today has earned so far, and where the
 * streak would stand once today counts.
 */

/** Local calendar day, which is what a streak and a daily goal are measured in. */
export function localDate(now = new Date()): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** XP earned today, summed from stored sessions. */
export async function xpToday(now = new Date()): Promise<number> {
  const today = localDate(now);
  const sessions = await db.sessions.toArray();
  return sessions
    .filter((s) => localDate(new Date(s.endedAt)) === today)
    .reduce((sum, s) => sum + s.xpEarned, 0);
}

/** Streak after a day's activity, and whether a milestone was reached. */
export function streakAfterToday(
  profile: Profile,
  now = new Date(),
): { streak: Economy.StreakResult; milestone: number | null } {
  const streak = Economy.updateStreak(
    {
      current: profile.streakCurrent,
      longest: profile.streakLongest,
      lastDate: profile.streakLastDate,
      restUsedOn: profile.streakRestUsedOn,
    },
    localDate(now),
  );
  return { streak, milestone: Economy.milestoneReached(profile.streakCurrent, streak.current) };
}

/**
 * Whether a rest day is available right now: none has been taken in the last
 * week. The streak screen says so, because knowing a missed day is covered
 * is the whole comfort of the rule.
 */
export function restDayAvailable(profile: Profile, now = new Date()): boolean {
  if (profile.streakRestUsedOn === null) return true;
  return (
    Economy.daysBetween(profile.streakRestUsedOn, localDate(now)) >= Economy.REST_DAY_SPAN_DAYS
  );
}

/** "2h 14m", for a countdown label. */
export function formatDuration(ms: number): string {
  const totalMinutes = Math.max(1, Math.ceil(ms / 60_000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes}m`;
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
}
